/* Live mode (voice + video) — real Qwen3-Omni realtime over WebRTC/WebSocket. */
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useDemoServices } from "../lib/DemoContext.jsx";
import ConsultationHistory from "./ConsultationHistory.jsx";
import VoiceWorkspace from "./VoiceWorkspace.jsx";
import { Ic } from "./Icons.jsx";
import { RealtimeClient } from "../lib/realtimeClient.js";
import { createCourseClient } from "../lib/courseClient.js";
import { isArchiveError, ARCHIVE_RETRY_MESSAGE, ARCHIVE_DISCONNECTED_MESSAGE } from "../lib/courseArchive.js";
import { localizeError } from "../product/auth/localizeError.ts";

function fmt(s) {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const r = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${r}`;
}

function thinkingTime(ms) {
  return `${Math.max(1, Math.round((Number(ms) || 0) / 1000))}s`;
}

function ReasoningBlock({ reasoning }) {
  if (!reasoning) return null;
  if (reasoning.status === "streaming") {
    return <div className="live-reasoning-pending" role="status">Thinking...</div>;
  }
  if (reasoning.status === "failed") return <div className="ap-error" role="alert">{reasoning.error || "回复未完成，请重试。"}</div>;
  if (!reasoning.text) return null;
  return (
    <details className="live-reasoning">
      <summary>
        <span>Thinking for {thinkingTime(reasoning.durationMs)}</span>
        <span className="live-reasoning-chev" aria-hidden="true">›</span>
      </summary>
      <div className="live-reasoning-text">{reasoning.text}</div>
    </details>
  );
}

// Portrait roster for the digital-human renderer. "" = use the server-configured
// default portrait. Concrete ids live in the FlashTalk server's portrait store;
// the selection is forwarded to the gateway as a per-session hint (?portrait=…)
// for when the backend honors it (today it drives DEFAULT_PORTRAIT).
const PORTRAITS = [
  { id: "", label: "默认" },
  { id: "portrait_a", label: "形象A" },
  { id: "portrait_b", label: "形象B" },
];

// The r10+ finetunes end the final counselling turn with a literal </end>
// (termination training) — never show it as text.
const stripEnd = (t) => (t || "").replace(/<\/end>\s*$/, "");

const COURSE_KEY = "oqc-course-id";
const PANES_KEY = "oqc-pane-w";       // {chat: px, agent: px} — 拖宽持久化

/* ---- agent panel (course mode's slow lane) --------------------------------
   The fast lane is the Omni reply streaming into the bubbles; this panel shows
   what the harness agents extract AROUND the conversation: the course state
   injected at session start (dossier / stage / focus / history overviews /
   homework) and the post-session archival (summary + updated profile). */

function KVList({ obj, depth = 0 }) {
  if (obj == null) return null;
  if (typeof obj !== "object") return <span className="ap-val">{String(obj)}</span>;
  if (Array.isArray(obj)) {
    return (
      <ul className="ap-list">
        {obj.map((v, i) => <li key={i}><KVList obj={v} depth={depth + 1} /></li>)}
      </ul>
    );
  }
  const entries = Object.entries(obj).filter(([, v]) => v != null && v !== "");
  if (!entries.length) return <span className="ap-val ap-empty">（空）</span>;
  return (
    <dl className="ap-kv">
      {entries.map(([k, v]) => {
        // object/array values fold up so a deep profile stays scannable;
        // top level starts open, nested levels start collapsed.
        const nested = typeof v === "object" &&
          (Array.isArray(v) ? v.length > 0 : Object.keys(v).length > 0);
        if (nested) {
          return (
            <details className="ap-sub" key={k} open={depth === 0}>
              <summary>{k}</summary>
              <div className="ap-sub-body"><KVList obj={v} depth={depth + 1} /></div>
            </details>
          );
        }
        return (
          <div className="ap-row" key={k}>
            <dt>{k}</dt>
            <dd><KVList obj={v} depth={depth + 1} /></dd>
          </div>
        );
      })}
    </dl>
  );
}

function ApSection({ title, children, open = false }) {
  return (
    <details className="ap-section" open={open}>
      <summary>{title}</summary>
      <div className="ap-section-body">{children}</div>
    </details>
  );
}

const GENDERS = ["女", "男", "其他"];

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("zh-CN", {
      month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
      hour12: false,
    });
  } catch { return iso; }
}

/* 来访者花名册：harness 的 course 列表按 user_id（昵称）分组；每个来访者可
   展开名下已归档的 session（删除 = 回滚到该次会话开始前）、删除整个课程，或
   接入继续咨询。新来访者走建档表单（称呼/性别/年龄 预载入档案）。 */
function ClientRoster({ model, activeId, busy, notice, onAttach, onCreate }) {
  const services = useDemoServices();
  const api = useMemo(() => services ? services.courseClient(model) : createCourseClient(model), [model, services]);
  const storageKey = `${COURSE_KEY}:${model}`;
  const [roster, setRoster] = useState(null);   // null = 加载中
  const [detail, setDetail] = useState({});     // course_id -> GET course 详情
  const [expanded, setExpanded] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", gender: "女", age: "", mainProblem: "" });
  const [err, setErr] = useState("");

  const refresh = useCallback(async () => {
    try {
      const j = await api.list();
      setRoster(j.courses || []);
      setErr("");
    } catch (e) {
      setRoster([]);
      setErr("加载来访者列表失败：" + e.message);
    }
  }, [api]);
  useEffect(() => { refresh(); }, [refresh]);

  const toggleSessions = useCallback(async (id) => {
    setExpanded((cur) => (cur === id ? "" : id));
    try {
      const j = await api.get(id);
      setDetail((d) => ({ ...d, [id]: j }));
    } catch { /* 列表仍可用 */ }
  }, [api]);
  const delCourse = useCallback(async (id, name) => {
    if (!window.confirm(`删除来访者「${name}」的整个课程（含所有会话记录）？`)) return;
    try {
      await api.remove(id);
      if (localStorage.getItem(storageKey) === id) localStorage.removeItem(storageKey);
      setDetail((d) => { const n = { ...d }; delete n[id]; return n; });
      refresh();
    } catch (e) { setErr("删除失败：" + e.message); }
  }, [api, refresh, storageKey]);
  const delSession = useCallback(async (id, k) => {
    if (!window.confirm(`删除第 ${k} 次及其后的会话记录？课程将回滚到第 ${k} 次开始前。`)) return;
    try {
      const j = await api.removeSession(id, k);
      setDetail((d) => ({ ...d, [id]: j }));
      refresh();
    } catch (e) { setErr("删除失败：" + e.message); }
  }, [api, refresh]);

  // 按 user_id 分组（同昵称的多次测试归到同一来访者名下）
  const groups = [];
  const byUser = new Map();
  for (const c of roster || []) {
    const uid = c.user_id || c.course_id;
    if (!byUser.has(uid)) { byUser.set(uid, []); groups.push(uid); }
    byUser.get(uid).push(c);
  }

  return (
    <div className="ap-setup">
      <p className="ap-hint">
        课程模式：<b>选定来访者后才会连接模型、开始对话</b>。网关从 harness 服务
        加载训练同款的疗程状态（档案 / 阶段 / 历史概览）；会话结束后由 agent
        慢通道归档提取。退出默认中断（不归档），课程停留在上次会话的结束状态。
      </p>
      {notice && <div className="ap-banner">{notice}</div>}
      <div className="ap-roster-head">
        <span>来访者（{groups.length}）</span>
        <button className="ap-btn small" onClick={refresh}>刷新</button>
        <button className="ap-btn small primary"
                onClick={() => services ? onCreate({}) : setShowForm((s) => !s)}>{services ? "＋ 新建实验（复用资料）" : "＋ 新来访者"}</button>
      </div>
      {showForm && (
        <div className="ap-form">
          <input placeholder="称呼（必填）" value={form.name} autoFocus
                 onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <div className="ap-form-row">
            <div className="seg">
              {GENDERS.map((g) => (
                <button key={g} className={form.gender === g ? "on" : ""}
                        onClick={() => setForm((f) => ({ ...f, gender: g }))}>{g}</button>
              ))}
            </div>
            <input type="number" placeholder="年龄" min="1" max="120" value={form.age}
                   onChange={(e) => setForm((f) => ({ ...f, age: e.target.value }))} />
          </div>
          <textarea className="ap-form-textarea" rows={3}
                    placeholder="主要想咨询的问题（必填）—— 来访者第 1 次会话开口说的第一句话"
                    value={form.mainProblem}
                    onChange={(e) => setForm((f) => ({ ...f, mainProblem: e.target.value }))} />
          <button className="ap-btn primary"
                  disabled={!form.name.trim() || !form.mainProblem.trim() || busy}
                  onClick={() => onCreate({
                    name: form.name.trim(), gender: form.gender,
                    age: form.age ? Number(form.age) : "",
                    mainProblem: form.mainProblem.trim(),
                  })}>
            建档并开始第 1 次会话
          </button>
        </div>
      )}
      {roster === null && <div className="ap-empty">来访者列表加载中…</div>}
      {roster !== null && !groups.length && !showForm && (
        <div className="ap-empty">还没有来访者：点「＋ 新来访者」建档开始。</div>
      )}
      {groups.map((uid) => {
        const cs = byUser.get(uid);
        const name = cs[0].client_name || uid;
        return (
          <div className="ap-client" key={uid}>
            <div className="ap-client-name">{name}
              {name !== uid && <span className="ap-client-uid">{uid}</span>}
            </div>
            {cs.map((c) => (
              <div key={c.course_id}
                   className={"ap-course" + (c.course_id === activeId ? " active" : "")}>
                <div className="ap-course-row">
                  <span className="chip">第 {c.next_session_index} 次</span>
                  <span className="ap-course-date">{fmtDate(c.created_at)}</span>
                  <button className="ap-btn small primary" disabled={busy}
                          onClick={() => onAttach(c.course_id)}>
                    {(c.finished_sessions || []).length ? "继续咨询" : "开始咨询"}
                  </button>
                  <button className="ap-btn small"
                          onClick={() => toggleSessions(c.course_id)}>
                    会话 {(c.finished_sessions || []).length}
                  </button>
                  <button className="ap-btn small danger" title="删除整个课程"
                          onClick={() => delCourse(c.course_id, name)}>删</button>
                </div>
                {expanded === c.course_id && (
                  <div className="ap-sess-list">
                    {!((detail[c.course_id] || {}).sessions || []).length
                      ? <span className="ap-empty">还没有已归档的会话</span>
                      : detail[c.course_id].sessions.map((s) => (
                          <div className="ap-sess" key={s.session_index}>
                            <div className="ap-sess-main">
                              <b>第 {s.session_index} 次</b>
                              {s.stage && <span className="ap-sess-stage">{s.stage}</span>}
                              <span className="ap-sess-turns">{s.turns} 轮</span>
                              {s.summary_abstract &&
                                <div className="ap-sess-abs">{s.summary_abstract}</div>}
                            </div>
                            <button className="ap-btn small danger"
                                    title={`删除第 ${s.session_index} 次及其后的记录（回滚）`}
                                    onClick={() => delSession(c.course_id, s.session_index)}>删</button>
                          </div>
                        ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}
      {err && <div className="ap-error">{err}</div>}
    </div>
  );
}

function AgentPanel({ model, course, onCreate, onAttach, onFinish, onInterrupt, locale = 'zh-CN',
                      panelRef, style, onRetryPreparation }) {
  const services = useDemoServices();
  const tr = (zh, en) => locale.startsWith('zh') ? zh : en;
  const api = useMemo(() => services ? services.courseClient(model) : createCourseClient(model), [services, model]);
  const [panel, setPanel] = useState("current");
  const start = course.start;
  const fin = course.finish;
  return (
    <aside className="live-agent" ref={panelRef} style={style}>
      <div className="live-chat-head">Agent 后台状态</div>
      <div className="ap-history-tabs" role="group" aria-label="咨询记忆">
        <button className={"ap-btn" + (panel === "current" ? " primary" : "")} aria-pressed={panel === "current"} onClick={() => setPanel("current")}>当前会话</button>
        <button className={"ap-btn" + (panel === "history" ? " primary" : "")} aria-pressed={panel === "history"} onClick={() => setPanel("history")}>历史咨询</button>
      </div>
      <div className="live-agent-body">
        {panel === "history" && <ConsultationHistory api={api} courseId={course.id} revision={fin} MemoryView={KVList} />}
        {panel === "current" && !start && (
          <>
            {services ? <>
              <p className="ap-hint" role="status">{course.error ? "咨询准备失败" : course.id ? "正在连接咨询，记忆将在会话开始后显示…" : "正在加载你的咨询资料…"}</p>
              {course.error && !course.id && <button className="ap-btn" onClick={onRetryPreparation}>重新连接</button>}
            </> : <ClientRoster model={model} activeId={course.id} busy={course.busy}
                          notice={course.notice}
                          onAttach={onAttach} onCreate={onCreate} />}
            {course.error && <div className="ap-error">{course.error}</div>}
          </>
        )}
        {panel === "current" && start && (
          <>
            <div className="ap-meta">
              {(start.client_info?.static_traits?.name) && (
                <span className="chip">{start.client_info.static_traits.name}</span>
              )}
              <span className="chip">第 {start.session_index} 次会话</span>
              <span className="ap-stage">{start.stage}</span>
              <div className="ap-course-id" title="course_id">{start.course_id}</div>
            </div>
            {start.counselor_speaks_first === false && !fin && (
              start.session_index === 1 && start.client_info?.main_problem ? (
                <div className="ap-hint">已自动把建档时填的「主要想咨询的问题」作为来访者的第
                  1 句话发出——等咨询师（模型）回复即可。</div>
              ) : (
                <div className="ap-hint">该模型的训练形态是来访者先开口——请直接开始说话。</div>
              )
            )}
            {course.liveProfile && (
              <ApSection title={`来访者档案（实时提取 · ${course.liveTurns || 0} 轮）`} open>
                <KVList obj={course.liveProfile} />
              </ApSection>
            )}
            <ApSection title="来访者档案（会话开始时）" open={!course.liveProfile}>
              {Object.keys(start.client_info || {}).length
                ? <KVList obj={start.client_info} />
                : <span className="ap-empty">首次会话：档案为空，由咨询中逐步建立</span>}
            </ApSection>
            <ApSection title={`本次聚焦目标（${(start.session_focus || []).length}）`}>
              <KVList obj={start.session_focus} />
            </ApSection>
            <ApSection title={`历史会话概览（${(start.history_list || []).length}）`}>
              {(start.history_list || []).length
                ? (start.history_list || []).map((h, i) => (
                    <div className="ap-hist" key={i}>
                      <div className="ap-hist-title">第 {i + 1} 次 · {h.session_stage || ""}</div>
                      <div className="ap-hist-abs">{h.session_summary_abstract}</div>
                    </div>
                  ))
                : <span className="ap-empty">（无）</span>}
            </ApSection>
            <ApSection title="上轮布置作业">
              <KVList obj={start.homework_assigned} />
            </ApSection>

            {course.endSignal && !fin && (
              <div className="ap-banner">模型给出了结束信号（&lt;/end&gt;），可以归档本次会话</div>
            )}
            {course.archiving && (
              <div className="ap-archiving">归档中：agent 正在提取会话摘要与来访者状态…</div>
            )}
            {course.archiveRetry && !course.archiving && <div className="ap-banner" role="status">{course.archiveQueued ? tr('后台归档尚未完成，可以退出后在咨询记录中查看或重试。', 'The background archive is not complete. You can leave and check or retry it in history.') : ARCHIVE_RETRY_MESSAGE}</div>}
            {fin && (
              <>
                <ApSection title="会话摘要（agent 提取）" open>
                  <div className="ap-abs">{fin.summary?.session_summary_abstract}</div>
                  {fin.summary?.client_state_analysis && (
                    <KVList obj={fin.summary.client_state_analysis} />
                  )}
                </ApSection>
                <ApSection title="布置作业 / 下次计划" open>
                  <KVList obj={{
                    作业: fin.summary?.homework,
                    下次阶段: fin.summary?.next_session_plan?.next_session_stage,
                    下次聚焦: fin.summary?.next_session_plan?.next_session_focus,
                  }} />
                </ApSection>
                <ApSection title="更新后的来访者档案">
                  <KVList obj={fin.profile} />
                </ApSection>
                <div className="ap-banner ok">已归档，下次会话为第 {fin.next_session_index} 次</div>
              </>
            )}
            {course.error && <div className="ap-error">{course.error}</div>}
            {!fin && (
              <div className="ap-actions">
                <button className="ap-btn primary" disabled={course.archiving || course.archiveDisconnected}
                        onClick={onFinish}>
                  {course.archiving ? "归档中…" : course.archiveRetry ? "重试归档" : "结束本次会话并归档"}
                </button>
                <button className="ap-btn" disabled={course.archivePending && !course.archiveQueued}
                        title={tr('退出并返回主页，已保存的进度可继续', 'Return home; saved progress can be resumed')}
                        onClick={onInterrupt}>{tr('退出', 'Exit consultation')}</button>
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
}

export default function LiveMode({ mode, tweaks, model, modelName, courseCapable = false,
                                   coursePromptVersion = "", initialCourseId = "", locale = "en", onProgress, onEnd }) {
  const services = useDemoServices();
  const courseApi = useMemo(() => services ? services.courseClient(model) : createCourseClient(model), [model, services]);
  const isVideo = mode === "video";
  const [elapsed, setElapsed] = useState(0);
  const [status, setStatus] = useState("connecting"); // connecting|listening|thinking|speaking|denied|closed
  const [muted, setMuted] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [messages, setMessages] = useState([]); // {id, role, text, reasoning?}
  const [textDraft, setTextDraft] = useState("");
  const tr = (zh, en) => locale.startsWith("zh") ? zh : en;
  const [liveError, setLiveError] = useState("");
  const [viz, setViz] = useState(tweaks.voiceViz || "orb");
  // Digital human: a talking portrait the gateway renders (FlashTalk), lip-synced
  // to the assistant's voice. Default transport (WS) delivers it as base64 JPEG
  // frames we paint onto a <canvas> — no WebRTC/ICE, so it works behind NAT. (If
  // the WebRTC transport is chosen it instead arrives as a remote video track,
  // held in `dhStream`.) `dhActive` flips true once the first frame/track lands.
  const [dhOn, setDhOn] = useState(!!tweaks.digitalHuman);
  const [portrait, setPortrait] = useState(tweaks.portrait || "");
  const [dhStream, setDhStream] = useState(null);
  const [dhActive, setDhActive] = useState(false);
  // course mode (harness): start/finish hold the agent panel's slow-lane data.
  // Authenticated sessions resolve the account-owned course before connecting.
  const [course, setCourse] = useState(() => ({
    id: courseCapable ? initialCourseId : "",
    start: null, finish: null, archiving: false, endSignal: false,
    archivePending: false, archiveQueued: false, archiveRetry: false, archiveDisconnected: false, contextWarning: null,
    busy: false, error: "", notice: "", liveProfile: null, liveTurns: 0,
  }));
  const [prepareAttempt, setPrepareAttempt] = useState(0);
  useEffect(() => {
    onProgress?.(course.id ? {
      courseId: course.id, sessionIndex: course.start?.session_index,
      archiveQueued: course.archiveQueued,
      status: course.archivePending ? (course.archiveDisconnected ? 'archive_disconnected' : course.archiveRetry ? 'archive_retry' : 'archiving')
        : course.finish ? 'finished' : status,
    } : null);
  }, [onProgress, course.id, course.start?.session_index, course.archivePending,
      course.archiveDisconnected, course.archiveRetry, course.archiveQueued, course.finish, status]);
  useEffect(() => {
    if (!services || !courseCapable || initialCourseId) return;
    let alive = true;
    setCourse(c => ({ ...c, error: "" }));
    courseApi.ensure().then(result => {
      if (!result.course_id) throw new Error("未能加载咨询资料，请重试");
      if (alive) setCourse(c => ({ ...c, id: result.course_id }));
    })
      .catch(error => { if (alive) setCourse(c => ({ ...c, error: error.message })); });
    return () => { alive = false; };
  }, [courseApi, courseCapable, initialCourseId, services, prepareAttempt]);
  // 课程模式的准入闸门：没选来访者就不连模型（不开麦、不建上游 session）。
  // 非课程模型恒为 true，行为不变。
  const courseGate = !courseCapable || !!course.id;
  // 三栏宽度（右两栏各自的像素宽，拖动分隔条调整，localStorage 持久化）
  const [paneW, setPaneW] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PANES_KEY)) || {}; }
    catch { return {}; }
  });

  const videoRef = useRef(null);
  const dhVideoRef = useRef(null);
  const dhCanvasRef = useRef(null);
  const dhActiveRef = useRef(false);
  const orbRef = useRef(null);
  const waveRef = useRef(null);
  const clientRef = useRef(null);
  const rafRef = useRef(0);
  const chatRef = useRef(null);
  const stateRef = useRef(status); stateRef.current = status;
  const muteRef = useRef(muted); muteRef.current = muted;
  // course at connect time, without making it a reconnect dependency
  const courseRef = useRef(course); courseRef.current = course;
  // message bookkeeping: bubbles are append-only and routed by the server's
  // stable ids (item_id per user segment, response_id per reply). A late or
  // out-of-order stream always patches its OWN bubble, so nothing overwrites or
  // vanishes, and consecutive segments stay in spoken order.
  const seqRef = useRef(0);
  const idMapRef = useRef(new Map());   // server id -> local message id

  // ---- timer ---- 计时从「真正开始咨询」起算，等待选来访者的时间不计入
  useEffect(() => {
    if (!courseGate) { setElapsed(0); return; }
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [courseGate]);

  // ---- message-list helpers (build a right-side chat transcript) ----
  const addMsg = useCallback((role, text, extra = {}) => {
    const id = ++seqRef.current;
    setMessages((m) => [...m, { id, role, text, ...extra }]);
    return id;
  }, []);
  const patchMsg = useCallback((id, patch) => {
    setMessages((m) => m.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }, []);
  // Create the bubble for `key` if it's new, else patch it in place. A null/
  // empty text on an existing bubble is ignored so a late empty placeholder
  // never wipes real transcript text.
  const ensureBubble = useCallback((role, key, text) => {
    const k = key || `${role}?`;
    const existing = idMapRef.current.get(k);
    if (existing == null) {
      idMapRef.current.set(k, addMsg(role, text || ""));
    } else if (text != null && text !== "") {
      patchMsg(existing, { text });
    }
  }, [addMsg, patchMsg]);
  const ensureReasoning = useCallback((key, reasoning) => {
    const k = key || "assistant?";
    const existing = idMapRef.current.get(k);
    if (existing == null) {
      idMapRef.current.set(k, addMsg("assistant", "", { reasoning }));
    } else {
      patchMsg(existing, { reasoning });
    }
  }, [addMsg, patchMsg]);

  // Paint an inbound digital-human JPEG frame (WS transport) onto the canvas.
  const drawFrame = useCallback((b64) => {
    const canvas = dhCanvasRef.current;
    if (!canvas) return;
    const img = new Image();
    img.onload = () => {
      const c = dhCanvasRef.current;
      if (!c) return;
      if (c.width !== img.naturalWidth) c.width = img.naturalWidth;
      if (c.height !== img.naturalHeight) c.height = img.naturalHeight;
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      if (!dhActiveRef.current) { dhActiveRef.current = true; setDhActive(true); }
    };
    img.src = "data:image/jpeg;base64," + b64;
  }, []);

  // ---- realtime client lifecycle ----
  useEffect(() => {
    // 课程模式且还没选来访者：什么都不建 —— 不 getUserMedia、不开 ws、上游也
    // 不会有 session。麦克风权限弹窗也因此推迟到真正开始咨询时才出现。
    if (!courseGate) { setStatus("waiting"); return; }
    // Each (re)connect gets a fresh RealtimeSession upstream, which restarts its
    // per-turn/per-reply ids (u1_1, r1, …) from zero. The id->bubble map persists
    // across reconnects, so without resetting it the new session's u1_1/r1 would
    // collide with the PREVIOUS session's bubbles and patch them in place — the
    // reported "new message overwrites the old one". Start a clean id space per
    // connection so reused server ids always open fresh bubbles (old ones stay).
    idMapRef.current = new Map();
    const makeClient = services ? services.realtimeClient : options => new RealtimeClient(options);
    const client = makeClient({
      mode,
      transport: tweaks.transport || "ws",
      voice: tweaks.voice || "Chelsie",
      model: model || "qwen3-omni",
      instructions: tweaks.systemPrompt || "",
      digitalHuman: dhOn,
      portrait,
      realtimeMode: tweaks.realtimeMode || "",
      thinking: tweaks.thinking,
      handlers: {
        onStatus: (s) => {
          setStatus(s);
          if (s === "thinking" || s === "speaking") setLiveError("");
          if (s === "closed") setMessages(items => items.map(item => item.reasoning?.status === "streaming"
            ? { ...item, reasoning: { status: "failed", error: "连接已断开，本轮回复未完成。" } } : item));
          if (s === "closed") setCourse((c) => c.archivePending && !c.finish
            ? { ...c, archiving: false, archiveRetry: false, archiveDisconnected: true,
                error: c.archiveQueued ? "" : ARCHIVE_DISCONNECTED_MESSAGE } : c);
        },
        // a new speech segment began -> pre-create its (empty) bubble so a
        // "🎤 语音消息" placeholder shows immediately, in order.
        onSpeechStart: (itemId) => { if (itemId) ensureBubble("user", itemId, ""); },
        onUserText: (t, _final, itemId) => ensureBubble("user", itemId, t),
        onHistoryMessage: ({ role, id, text }) => ensureBubble(role, id, text),
        onReasoningStart: (respId) => ensureReasoning(respId, {
          status: "streaming", text: "", durationMs: null,
        }),
        onReasoningText: (text, final, respId, durationMs) => {
          if (!final) return; // keep the compact Thinking... state while streaming
          ensureReasoning(respId, text ? {
            status: "done", text, durationMs,
          } : null);
        },
        onAssistantText: (acc, _final, respId) => ensureBubble("assistant", respId, acc),
        onRemoteVideo: (stream) => { setDhStream(stream); if (stream) { dhActiveRef.current = true; setDhActive(true); } },
        onVideoFrame: (b64) => drawFrame(b64),
        onAgentUpdate: (kind, data) => {
          if (kind === 'session.interrupt') { clientRef.current?.stop(); onEnd(); return; }
          setCourse((c) => {
            if (kind === "session.start") {
              return { ...c, id: data.course_id || c.id, start: data,
                       finish: null, archiving: false, endSignal: false,
                       archivePending: false, archiveQueued: false, archiveRetry: false, archiveDisconnected: false, contextWarning: null,
                       error: "", notice: "", liveProfile: null, liveTurns: 0 };
            }
            if (kind === "profile.live") {
              return { ...c, liveProfile: data.profile, liveTurns: data.turns };
            }
            if (kind === "session.archive_queued") return { ...c, archiving: true, archivePending: true, archiveQueued: true, archiveRetry: false };
            if (kind === "session.finishing") return { ...c, archiving: true, archivePending: true, archiveRetry: false };
            if (kind === "session.finish") {
              return { ...c, archiving: false, archivePending: false, archiveRetry: false, finish: data, error: "" };
            }
            if (kind === "end_signal") return { ...c, endSignal: true };
            if (kind === "context.warning") return { ...c, contextWarning: data };
            return c;
          });
        },
        onError: (m, error) => {
          console.warn("[realtime]", m, { code: error?.code || error?.type, close_code: error?.close_code, trace_id: error?.trace_id });
          const message = error?.code ? localizeError(error, locale, m) : m;
          setLiveError(message);
          if (!isArchiveError(error) && !clientRef.current?.archivePending) {
            setMessages(items => items.map(item => item.reasoning?.status === "streaming"
              ? { ...item, reasoning: { status: "failed", error: message } } : item));
          }
          if (isArchiveError(error) || clientRef.current?.archivePending || /harness|课程|归档/.test(m)) {
            setCourse((c) => {
              const archivePending = error?.type !== "empty_session" && (isArchiveError(error) || c.archivePending);
              return { ...c, busy: false, archiving: false, archivePending, archiveRetry: archivePending, error: message };
            });
          }
        },
      },
      courseId: courseCapable ? courseRef.current.id : "",
    });
    clientRef.current = client;

    client.start().then(() => {
      if (client.closed || clientRef.current !== client) return;
      if (isVideo && videoRef.current && client.stream) {
        videoRef.current.srcObject = client.stream;
        videoRef.current.play().catch(() => {});
      }
    });

    // visualization loop
    const data = (level) => {
      if (orbRef.current) {
        const st = stateRef.current;
        if (!isVideo && !dhOn) {
          // Use the existing input analyser; keep the new orb's motion subtle.
          const scale = 1 + (st === "listening" && level > 0.025 ? Math.min(0.045, level * 0.15) : 0);
          orbRef.current.style.setProperty('--voice-input-scale', scale.toFixed(3));
        } else {
          const base = st === "speaking" ? 1.06 : 1.0;
          const k = st === "speaking" ? 0.14 + Math.random() * 0.06 : 0.34 * level;
          orbRef.current.style.transform = `scale(${(base + k).toFixed(3)})`;
        }
      }
      if (waveRef.current) {
        const bars = waveRef.current.children;
        for (let i = 0; i < bars.length; i++) {
          const h = 5 + level * 22 * (0.6 + Math.random() * 0.8);
          bars[i].style.height = h.toFixed(1) + "px";
        }
      }
    };
    const loop = () => {
      const lvl = muteRef.current ? 0 : (clientRef.current ? clientRef.current.getInputLevel() : 0);
      data(lvl);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      if (!client.closed) client.stop();
      clientRef.current = null;
      setDhStream(null);
      dhActiveRef.current = false;
      setDhActive(false);
    };
    // Reconnect when the session-shaping inputs change: voice changes
    // resend session.update through a fresh connection; toggling the digital
    // human or switching portrait both require a fresh connection (the DH request
    // + portrait are sent as hints when the transport is established).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // NB: tweaks.thinking is deliberately NOT a dep — it is pushed with a
    // session.update below. Having it here tore the call down mid-turn (the user
    // saw "Disconnected" before the reply arrived, and the pending voice message
    // never got its transcript).
    // courseGate IS a dep: 选中来访者 -> false→true -> 这里才真正连上；中断
    // （course.id 清空）-> true→false -> 走 cleanup 断开，退回花名册。它是布尔
    // 值而不是 course.id，所以【换】课程仍走 setCourse 原地切换，不重连。
  }, [mode, dhOn, portrait, tweaks.voice, tweaks.realtimeMode, courseGate, services ? course.id : null]);

  // Thinking switch flipped during a call: update the live session in place.
  useEffect(() => {
    clientRef.current?.setThinking?.(tweaks.thinking);
  }, [tweaks.thinking]);

  // 记录当前接入的 course（花名册删课程时用来清理这条记录）。不再用于自动
  // 接入 —— 进入语音页必须重新在花名册里选来访者。
  useEffect(() => {
    if (course.id && !services) localStorage.setItem(`${COURSE_KEY}:${model}`, course.id);
  }, [course.id, model]);

  // 换来访者 = 换一次会话：清掉上一位来访者的对话记录，避免两个人的话串在
  // 同一列。中断（id 清空）时同样清掉。
  useEffect(() => {
    if (!courseCapable) return;
    setMessages([]);
    idMapRef.current = new Map();
  }, [courseCapable, course.id]);

  const createCourse = useCallback(async (form) => {
    setCourse((c) => ({ ...c, busy: true, error: "" }));
    try {
      // prompt 版本跟模型注册表走（/api/models 的 course_prompt_version），
      // 不在前端写死：r8 = v0（患者先开口、无 opener——给它 v1 的「这是第k次
      // 会话」开场实测 6/6 编造出上次咨询/作业），r10+ = v1。
      // 建档表单（称呼/性别/年龄）预载入 static_traits —— 键名与 v1 模板的
      // 「基本信息」区块一致；v0 渲染为档案摘要的「来访者:…」行。
      // main_problem 是 r8 训练格式里来访者开口的第一句话（患者先开口、无
      // opener）：网关在 session.py::_start_course 里对 session 1 自动把它
      // 当成来访者的第一轮发出，建档时填一次，不用真人对着麦克风再说一遍。
      const traits = {};
      if (form?.name) traits.name = form.name;
      if (form?.gender) traits.gender = form.gender;
      if (form?.age) traits.age = form.age;
      const clientInfo = {};
      if (Object.keys(traits).length) clientInfo.static_traits = traits;
      if (form?.mainProblem) clientInfo.main_problem = form.mainProblem;
      const j = await courseApi.create({
        modality: "cbt",
        ...(coursePromptVersion ? { prompt_version: coursePromptVersion } : {}),
        client_name: form?.name || "",
        ...(Object.keys(clientInfo).length ? { client_info: clientInfo } : {}),
      });
      if (!j.course_id) throw new Error(j.error || "未返回课程编号");
      setCourse((c) => ({ ...c, id: j.course_id, busy: false }));
      // 建档时通常还没连（courseGate 关着）—— id 一落地闸门就开，连接 effect
      // 会带着 course_id 建立 session。setCourse 只在【已连接时换课程】起作用。
      if (!services) clientRef.current?.setCourse?.(j.course_id);
    } catch (e) {
      setCourse((c) => ({ ...c, busy: false, error: "课程创建失败：" + e.message }));
    }
  }, [courseApi, coursePromptVersion]);
  const attachCourse = useCallback((id) => {
    setCourse((c) => ({ ...c, id, error: "" }));
    if (!services) clientRef.current?.setCourse?.(id);
  }, [services]);
  const finishCourse = useCallback(() => {
    if (clientRef.current?.finishCourse?.()) {
      setCourse((c) => ({ ...c, archiving: true, archivePending: true, archiveRetry: false, error: "" }));
    } else {
      setCourse((c) => ({ ...c, error: "当前无法提交归档，请检查连接或等待正在进行的归档完成。" }));
    }
  }, []);
  const exitCourse = () => {
    if (course.archivePending && !course.archiveQueued) return;
    clientRef.current?.stop();
    onEnd();
  };

  // ---- 三栏分隔条拖动（右两栏改像素宽；≤860px 竖排布局下不渲染） ----
  const chatPaneRef = useRef(null);
  const agentPaneRef = useRef(null);
  const dragRef = useRef(null);
  const startDrag = useCallback((pane) => (e) => {
    e.preventDefault();
    const el = pane === "chat" ? chatPaneRef.current : agentPaneRef.current;
    dragRef.current = { pane, startX: e.clientX,
                        startW: el ? el.offsetWidth : 320 };
    const move = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      // 分隔条在面板左缘：左拖变宽、右拖变窄
      const w = Math.min(760, Math.max(220, d.startW + (d.startX - ev.clientX)));
      setPaneW((p) => ({ ...p, [d.pane]: w }));
    };
    const up = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setPaneW((p) => {
        try { localStorage.setItem(PANES_KEY, JSON.stringify(p)); } catch {}
        return p;
      });
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  // attach the inbound digital-human portrait track to its <video>
  useEffect(() => {
    const el = dhVideoRef.current;
    if (!el) return;
    el.srcObject = dhStream || null;
    if (dhStream) el.play().catch(() => {});
  }, [dhStream]);

  // propagate mute to the client (muteRef itself is declared up top for the loop)
  useEffect(() => { clientRef.current && clientRef.current.setMuted(muted); }, [muted]);

  // keep the chat panel pinned to the latest message
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  // close on Esc
  useEffect(() => {
    if (!isVideo && !dhOn) return;
    const h = (e) => { if (e.key === "Escape" && !e.defaultPrevented) exitCourse(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  const statusText = course.finish ? tr("本次会话已归档", "Session archived") : course.archivePending
    ? (course.archiveQueued ? tr("已交给后台归档，可以退出；进度见咨询记录", "Archiving in the background. You can leave and check progress in history.") : course.archiveDisconnected ? tr("连接已断开，归档待核对", "Disconnected — archive unconfirmed") : course.archiving ? tr("正在提交归档，请稍候", "Submitting archive, please wait") : tr("归档待确认，请重试", "Archive unconfirmed — retry")) : {
    waiting: course.error ? tr("咨询准备失败，请打开详情重试", "Preparation failed — open Details to retry") : tr("正在准备咨询…", "Preparing consultation…"),
    connecting: tr("正在连接…", "Connecting…"),
    listening: muted ? tr("已静音，可以打字交流", "Muted — you can still type") : tr("正在聆听", "Listening"),
    thinking: tr("正在思考…", "Thinking…"),
    speaking: tr("正在回复…", "Responding…"),
    denied: tr("麦克风不可用，请检查权限", "Microphone unavailable — check permissions"),
    closed: tr("连接已断开", "Disconnected"),
  }[status];

  const canSendText = !!clientRef.current?.canSendText && status === "listening" && courseGate && !course.archivePending && !course.finish;
  const sendText = () => {
    if (!canSendText || !textDraft.trim()) return;
    if (clientRef.current?.sendText(textDraft)) setTextDraft("");
    else setLiveError(tr("暂时无法发送，文字已保留。", "Unable to send right now. Your draft is saved."));
  };

  const active = status === "listening" || status === "speaking";

  const settingsPanel = (
        <div className="live-settings">
          {(isVideo || dhOn) && <div className="ls-row">
            <span className="ls-label">Visualization</span>
            <div className="seg">
              <button className={viz === "orb" ? "on" : ""} onClick={() => setViz("orb")}>Orb</button>
              <button className={viz === "wave" ? "on" : ""} onClick={() => setViz("wave")}>Wave</button>
            </div>
          </div>}
          {(isVideo || dhOn) && <div className="ls-row">
            <span className="ls-label">Transport</span>
            <div className="seg"><button className="on">{tweaks.transport || "ws"}</button></div>
          </div>}
          <div className="ls-row">
            <span className="ls-label">{tr("数字人", "Digital human")}</span>
            <div className="seg">
              <button className={dhOn ? "on" : ""} onClick={() => setDhOn(true)}>On</button>
              <button className={!dhOn ? "on" : ""} onClick={() => setDhOn(false)}>Off</button>
            </div>
          </div>
          {dhOn && (
            <div className="ls-row">
              <span className="ls-label">形象</span>
              <div className="seg">
                {PORTRAITS.map((p) => (
                  <button key={p.id || "default"} className={portrait === p.id ? "on" : ""}
                          onClick={() => setPortrait(p.id)}>{p.label}</button>
                ))}
              </div>
            </div>
          )}
        </div>
  );

  const agentPanel = courseCapable ? <AgentPanel model={model} locale={locale} course={course} onCreate={createCourse}
    onAttach={attachCourse} onFinish={finishCourse} onInterrupt={exitCourse}
    onRetryPreparation={() => setPrepareAttempt(n => n + 1)} /> : null;
  if (!isVideo && !dhOn) return <VoiceWorkspace locale={locale} modelName={modelName} elapsed={fmt(elapsed)}
    visualRef={orbRef}
    status={status} statusText={statusText} muted={muted || course.archivePending || !!course.finish}
    muteDisabled={!courseGate || course.archivePending || !!course.finish} onMute={() => setMuted(m => !m)} onEnd={exitCourse}
    messages={messages} draft={textDraft} onDraft={setTextDraft} onSend={sendText} canSend={canSendText}
    error={liveError || course.error} warning={course.contextWarning && !course.finish && (course.contextWarning.message || tr('本次会话已接近上下文上限，请结束并归档后开始下一次会话。', 'This session is nearing its context limit. Finish and archive it before starting another session.'))} course={course} onFinish={finishCourse}
    agentPanel={agentPanel} settingsPanel={settingsPanel} renderMessage={m => <div key={m.id} className={"live-msg " + m.role}>
      {m.role === "assistant" ? <><ReasoningBlock reasoning={m.reasoning} />
        {m.text ? <div className="live-answer-text">{stripEnd(m.text)}</div> : (!m.reasoning ? "…" : null)}</>
        : (m.text || tr("语音消息", "Voice message"))}
    </div>} />;

  return (
    <div className="live">
      <div className="live-main">
      <div className="live-timer">{fmt(elapsed)}</div>

      <div className="live-stage">
        {dhOn ? (
          /* Digital-human talking portrait plus the user's own camera. In video
             mode they sit side-by-side as phone-shaped portrait windows, so the
             counselor and user never cover each other. */
          <div className={"dh-layout" + (isVideo ? " two-up" : "") }>
            <div className="dh-frame phone-frame">
              {dhStream ? (
                <video ref={dhVideoRef} playsInline autoPlay muted></video>
              ) : (
                <canvas ref={dhCanvasRef} className="dh-canvas"></canvas>
              )}
              {!dhActive && (
                <div className="dh-waiting">
                  <div className={"orb dh-orb " + status}></div>
                  <span>数字人连接中…</span>
                </div>
              )}
              <span className="live-dot"><i></i> 咨询师</span>
            </div>
            {isVideo && (
              <div className="self-frame phone-frame">
                <video ref={videoRef} playsInline muted></video>
                {status === "denied" && (
                  <div className="cam-off"><Ic.CameraFlip size={40} /><span>Camera unavailable</span></div>
                )}
                <span className="live-dot"><i></i> 用户</span>
              </div>
            )}
          </div>
        ) : isVideo ? (
          <div className="video-frame">
            <video ref={videoRef} playsInline muted></video>
            {status === "denied" && (
              <div className="cam-off"><Ic.CameraFlip size={40} /><span>Camera unavailable</span></div>
            )}
            <span className="live-dot"><i></i> LIVE</span>
            <div className="cam-thumb-orb"></div>
          </div>
        ) : viz === "orb" ? (
          <div className={"orb-wrap" + (active ? " active" : "")}>
            <span className="orb-ring"></span>
            <div ref={orbRef} className={"orb " + status}></div>
          </div>
        ) : (
          <div ref={waveRef} className="wave">
            {Array.from({ length: 28 }).map((_, i) => <i key={i}></i>)}
          </div>
        )}
      </div>

      <div className="live-status">
        <div className={"live-eq" + (active ? " active" : "")}>
          {Array.from({ length: 5 }).map((_, i) => <i key={i}></i>)}
        </div>
        <div className="status-text">{statusText}</div>
      </div>

      {!services && !courseGate && (
        <div className="live-gate">
          <div className="live-gate-title">选择来访者后才开始咨询</div>
          <div className="live-gate-sub">
            在右侧「Agent 后台状态」里点「继续咨询」接上已有来访者，
            或「＋ 新来访者」填写称呼与主要想咨询的问题建档。
            <br />选定之前不会连接 {modelName || "模型"}，也不会打开麦克风。
          </div>
        </div>
      )}

      {showSettings && settingsPanel}

      <div className="live-controls">
        <div className="left">
          <button className="lc-btn end" title={tr('退出', 'Exit')} disabled={course.archivePending && !course.archiveQueued} onClick={exitCourse}><Ic.Close size={24} /></button>
          <button className="lc-btn" title="Settings" onClick={() => setShowSettings((s) => !s)}><Ic.Sliders size={23} /></button>
        </div>
        <div className="live-model" style={{ textAlign: "center" }}>{modelName || "Qwen3-Omni"}</div>
        <div className="right">
          <button className={"lc-btn" + (muted || course.archivePending || course.finish ? " muted" : "")} title="Mute"
                  disabled={!courseGate || course.archivePending || !!course.finish}
                  onClick={() => setMuted((m) => !m)}>
            {muted || course.archivePending || course.finish ? <Ic.MicOff size={24} /> : <Ic.Mic size={24} />}
          </button>
        </div>
      </div>
      </div>{/* /live-main */}

      {/* conversation transcript — an in-flow column docked to the right (not a
          floating overlay) so it never covers the orb/video on the left. */}
      <div className="live-splitter" title="拖动调整宽度"
           onMouseDown={startDrag("chat")} />
      <aside className="live-chat" ref={chatPaneRef}
             style={paneW.chat ? { flex: `0 0 ${paneW.chat}px` } : undefined}>
        <div className="live-chat-head">对话记录</div>
        {liveError && <div className="ap-error" role="alert">{liveError}</div>}
        {course.contextWarning && !course.finish && <div className="ap-banner" role="status">{course.contextWarning.message || "本次会话已接近上下文上限，请结束并归档后开始下一次会话。"}</div>}
        <div className="live-chat-body" ref={chatRef}>
          {messages.length === 0 ? (
            <div className="live-chat-empty">说点什么，对话会显示在这里</div>
          ) : (
            messages.map((m) => (
              <div key={m.id} className={"live-msg " + m.role}>
                {m.role === "assistant" ? (
                  <>
                    <ReasoningBlock reasoning={m.reasoning} />
                    {m.text ? <div className="live-answer-text">{stripEnd(m.text)}</div>
                      : (!m.reasoning ? "…" : null)}
                  </>
                ) : (m.text || "🎤 语音消息")}
              </div>
            ))
          )}
        </div>
        <form className="live-text-composer" onSubmit={event => { event.preventDefault(); sendText(); }}>
          <textarea rows={2} maxLength={12000} value={textDraft}
            aria-label={tr("在语音对话中输入文字", "Type in the voice conversation")}
            placeholder={tr("也可以打字交流…", "You can also type here…")}
            onChange={event => setTextDraft(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing && event.keyCode !== 229) {
                event.preventDefault(); sendText();
              }
            }} />
          <div className="live-text-actions">
            <span>{canSendText ? tr("Enter 发送 · Shift+Enter 换行", "Enter to send · Shift+Enter for a new line")
              : tr("等待会话就绪后发送", "Send when the conversation is ready")}</span>
            <button type="submit" disabled={!canSendText || !textDraft.trim()}>{tr("发送", "Send")}</button>
          </div>
        </form>
      </aside>

      {/* third column — the agent slow lane (course mode only): what the
          harness injects at session start and what its archival agents
          extract at the end, next to the Omni fast lane in the middle. */}
      {courseCapable && (
        <>
          <div className="live-splitter" title="拖动调整宽度"
               onMouseDown={startDrag("agent")} />
          <AgentPanel model={model} locale={locale} course={course} onCreate={createCourse}
                      onAttach={attachCourse} onFinish={finishCourse}
                      onInterrupt={exitCourse}
                      onRetryPreparation={() => setPrepareAttempt(n => n + 1)}
                      panelRef={agentPaneRef}
                      style={paneW.agent ? { flex: `0 0 ${paneW.agent}px` } : undefined} />
        </>
      )}
    </div>
  );
}
